import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
const badges = ['+200 alumnas', 'Método probado', 'Resultados reales'];
const COUNTDOWN_DURATION = 48 * 60 * 60 * 1000; // 48 hours in ms
const STORAGE_KEY = 'launch_countdown_end';
function getEndTime(): number {
  if (typeof window === 'undefined') return Date.now() + COUNTDOWN_DURATION;
  const stored = localStorage.getItem(STORAGE_KEY);
  const now = Date.now();
  if (stored) {
    const end = parseInt(stored, 10);
    if (!isNaN(end) && end > now) return end;
  }
  const newEnd = now + COUNTDOWN_DURATION;
  localStorage.setItem(STORAGE_KEY, String(newEnd));
  return newEnd;
}
function formatTimeLeft(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  return {
    hh: String(hours).padStart(2, '0'),
    mm: String(minutes).padStart(2, '0'),
    ss: String(seconds).padStart(2, '0')
  };
}
export function WhoGuidesYou() {
  const [endTime, setEndTime] = useState<number>(() => getEndTime());
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      const current = Date.now();
      if (current >= endTime) {
        // Reset
        const newEnd = current + COUNTDOWN_DURATION;
        localStorage.setItem(STORAGE_KEY, String(newEnd));
        setEndTime(newEnd);
        setNow(current);
      } else {
        setNow(current);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  const {
    hh,
    mm,
    ss
  } = formatTimeLeft(endTime - now);
  return <>
      <section className="py-20 md:py-28" style={{
      backgroundColor: '#2C1A0E'
    }} data-id="element-240">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-241">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl p-6 md:p-10 flex flex-col sm:flex-row items-center sm:items-start gap-6 md:gap-8" style={{
          backgroundColor: '#3A2415',
          border: '1.5px solid #C9A96E'
        }} data-id="element-242">
            {/* Profile Photo */}
            <div className="flex-shrink-0" data-id="element-243">
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden" style={{
              border: '3px solid #C9A96E',
              boxShadow: '0 0 20px rgba(201, 169, 110, 0.2)'
            }} data-id="element-244">
                <img src="https://cdn.magicpatterns.com/uploads/ghbD1DrKoQQStSymAmbzmn/TikTok_·_Pinch_of_Yum.jpg" alt="Catalina - Fundadora de La Cocina que Vende" className="w-full h-full object-cover" data-id="element-245" />
              </div>
            </div>

            {/* Text Content */}
            <div className="text-center sm:text-left" data-id="element-246">
              <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4" style={{
              color: '#C9A96E'
            }} data-id="element-247">
                ¿Quién te guía?
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-6" style={{
              color: '#F5F0EB'
            }} data-id="element-248">
                Soy Catalina. Empecé vendiendo meriendas congeladas desde mi
                casa sin experiencia, sin local y sin inversión grande. En menos
                de dos años construí un sistema que ya ayudó a más de 200
                mujeres en toda Argentina a generar sus primeros ingresos reales
                cocinando desde casa.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-4 md:gap-6" data-id="element-249">
                {badges.map((badge, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3 + index * 0.1
              }} className="flex items-center gap-2" data-id="element-250">
                    <CheckIcon className="w-4 h-4 flex-shrink-0" style={{
                  color: '#C9A96E'
                }} data-id="element-251" />
                    <span className="text-sm font-medium" style={{
                  color: '#F5F0EB'
                }} data-id="element-252">
                      {badge}
                    </span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-3 gap-3 sm:gap-4" style={{
      backgroundColor: '#3A1010'
    }} data-id="element-253">
        <div className="flex items-center gap-3 flex-wrap justify-center" data-id="element-254">
          <div className="flex items-center gap-2" data-id="element-255">
            <span className="text-lg" data-id="element-256">🔥</span>
            <span className="font-bold text-sm md:text-base" style={{
            color: '#E85D5D'
          }} data-id="element-257">
              Precio de lanzamiento — últimas horas
            </span>
          </div>
          <div className="flex items-center gap-1 px-3 py-1 rounded-md font-mono font-bold text-sm md:text-base tabular-nums" style={{
          backgroundColor: '#2A0A0A',
          color: '#FFB8B8',
          border: '1px solid #5A1A1A'
        }} aria-label="Tiempo restante de la oferta" data-id="element-258">
            <span data-id="element-259">{hh}</span>
            <span style={{
            color: '#E85D5D'
          }} data-id="element-260">
              :
            </span>
            <span data-id="element-261">{mm}</span>
            <span style={{
            color: '#E85D5D'
          }} data-id="element-262">
              :
            </span>
            <span data-id="element-263">{ss}</span>
          </div>
        </div>
        <a href="https://lacocinaquevende.shop/cart/53476221124881:1" className="font-bold text-sm md:text-base px-6 py-2.5 rounded-lg transition-opacity hover:opacity-90 whitespace-nowrap" style={{
        backgroundColor: '#C9A96E',
        color: '#2C1A0E'
      }} data-id="element-264">
          Quiero empezar ahora →
        </a>
      </div>
    </>;
}